import React, { useState, useEffect, useRef, FC, JSX, ChangeEvent, FormEvent } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import DatePicker from '../../components/ui/DatePicker';
import BackendStatus from '../../components/ui/BackendStatus';
import brandLogo from '../../assets/dhoondh2-logo.svg';
import { LocalStorageUser } from '../../types/localStorage.d';
import Toast from '../../components/ui/Toast';
import Select from '../../components/ui/Select';

interface RegisterProps {
  // No props defined in the original component, but keeping the interface for consistency
}

const Register: FC<RegisterProps> = (): JSX.Element => {
  const location = useLocation();
  useEffect(()=>{
    const queryParams = new URLSearchParams(location.search);
    const isCompany = queryParams.get("isCompany");
    const isJobseeker = queryParams.get("isJobseeker");
    if(isCompany){
      setFormData(prev => ({ ...prev, role: 'Company' }))
    } else if (isJobseeker) {
      setFormData(prev => ({ ...prev, role: 'Jobseeker' }))
    }

  },[location.search])
  const [formData, setFormData] = useState<{
    firstName: string;
    lastName: string;
    mobileNumber: string;
    dateOfBirth: string;
    role: LocalStorageUser['role'] | null;
  }>({
    firstName: '',
    lastName: '',
    mobileNumber: '',
    dateOfBirth: '',
    role: null,
  });
  const [errors, setErrors] = useState<{
    firstName?: string;
    lastName?: string;
    mobileNumber?: string;
    dateOfBirth?: string;
    submit?: string;
    role?: string;
  }>({});
  const [loading, setLoading] = useState<boolean>(false);
  const [isVisible, setIsVisible] = useState<boolean>(false);
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' | 'warning' | 'info' } | null>(null);
  const heroRef = useRef<HTMLDivElement>(null);

  const { register } = useAuth();
  const navigate = useNavigate();

  // Animation on scroll
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    if (heroRef.current) {
      observer.observe(heroRef.current);
    }

    return () => {
      if (heroRef.current) {
        observer.unobserve(heroRef.current);
      }
    };
  }, []);

  const handleChange = (e: ChangeEvent<HTMLInputElement | HTMLSelectElement>): void => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error when user starts typing
    if (errors[name as keyof typeof errors]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = (): boolean => {
    const newErrors: {
      firstName?: string;
      lastName?: string;
      email?: string;
      role?: string;
      mobileNumber?: string;
      dateOfBirth?: string;
      submit?: string;
    } = {};

    if (!formData.firstName.trim()) {
      newErrors.firstName = 'First name is required';
    }

    if (!formData.lastName.trim()) {
      newErrors.lastName = 'Last name is required';
    }

    if (!formData.mobileNumber) {
      newErrors.mobileNumber = 'Phone number is required';
    } else if (formData.mobileNumber.length != 10){
      newErrors.mobileNumber = 'please enter an valid 10 digit phone number';
    }
    if (!formData.role) {
      newErrors.role = 'Role is required';
    }
    if (formData.role === 'Jobseeker' || formData.role === 'Intern') {
      if (!formData.dateOfBirth) {
        newErrors.dateOfBirth = 'Date of birth is required';
      } else {
        const dob = new Date(formData.dateOfBirth);
        const today = new Date();
        const age = today.getFullYear() - dob.getFullYear() - (today < new Date(today.getFullYear(), dob.getMonth(), dob.getDate()) ? 1 : 0);
        if (isNaN(age) || (age < 18 && formData.role === 'Jobseeker')) {
          newErrors.dateOfBirth = 'You must be at least 18 years old';
        }
        if (isNaN(age) || (age < 16 && formData.role === 'Intern')) {
          newErrors.dateOfBirth = 'You must be at least 16 years old';
        }
      }
    }

    // Password is set after OTP verification; no password validation here

    setErrors(newErrors);
    console.log("validateForm", newErrors)
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();
    
    if (!validateForm()) return;

    setLoading(true);
    setErrors({}); // Clear previous errors
    
    try {
      console.log('Attempting registration with data:', formData);
      if (formData.role != null){
        const result = await register({...formData, role : formData.role});
        console.log('Registration successful:', result);
        
        localStorage.setItem('registerUserId', result.userId);
        
        // Flag to show onboarding message on first dashboard visit
        localStorage.setItem('userJustRegistered', 'true');
        
        // Show success toast and move to password setup after short delay
        setToast({ message: 'Account created! Continue to set your password.', type: 'success' });
        setTimeout(() => {
          navigate('/password-setup', { state: { mobileNumber: formData.mobileNumber , userId: result.userId , requestType : "SignUp" } });
        }, 1000);
      }
    } catch (error: any) {
      console.error('Registration failed:', error);
      setErrors({ submit: error.message || 'Registration failed' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen pb-20 md:pb-0 bg-gradient-to-br from-primary-lightGreen/5 via-white to-primary-lightGreen/10">
      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          position="top-right"
          duration={2500}
          onClose={() => setToast(null)}
          show
        />
      )}
      {/* Hero Section */}
      <section ref={heroRef} className="relative pt-20 pb-16 md:pt-24 md:pb-20 overflow-hidden">
        {/* Background Decorations */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute -top-40 -right-40 w-80 h-80 bg-primary-lightGreen/20 rounded-full blur-3xl"></div>
          <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-primary-lightGreen/20 rounded-full blur-3xl"></div>
          <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-primary-lightGreen/10 rounded-full blur-3xl"></div>
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className={`text-center transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            <div className="mx-auto flex items-center justify-center mb-8">
              <img src={brandLogo} alt="Dhoondh" className="h-12 md:h-16 aspect-auto" />
            </div>
            <h1 className="text-4xl md:text-6xl lg:text-7xl font-bold text-primary-black mb-6 leading-tight">
              Join
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary-lightGreen to-primary-lightGreen">
                {' '}Dhoondh
              </span>
            </h1>
            <p className="text-lg md:text-xl text-gray-600 max-w-2xl mx-auto mb-8 leading-relaxed">
              Create your account and start your journey to find the perfect job
            </p>
          </div>
        </div>
      </section>

      {/* Registration Form Section */}
      <section className="relative pb-20 md:pb-32">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
          <BackendStatus />
          <div className="bg-white/80 backdrop-blur-sm rounded-3xl p-8 md:p-10 shadow-xl border border-gray-100">
            <div className="text-center mb-8">
              <h2 className="text-2xl md:text-3xl font-bold text-primary-black mb-2">
                Create your account
              </h2>
              <p className="text-gray-600">
                Fill in your details to get started
              </p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="space-y-5">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <label className="block text-sm font-semibold text-gray-700">
                      First Name *
                    </label>
                    <input
                      type="text"
                      name="firstName"
                      value={formData.firstName}
                      onChange={handleChange}
                      className={`w-full px-4 py-3 rounded-xl border-2 transition-all duration-200 focus:outline-none focus:ring-4 focus:ring-primary-teaGreen/20 ${
                        errors.firstName 
                          ? 'border-red-300 focus:border-red-500' 
                          : 'border-gray-200 focus:border-primary-teaGreen'
                      }`}
                      placeholder="First name"
                      required
                    />
                    {errors.firstName && (
                      <p className="text-sm text-red-600 flex items-center gap-1">
                        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1_0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                        </svg>
                        {errors.firstName}
                      </p>
                    )}
                  </div>

                  <div className="space-y-2">
                    <label className="block text-sm font-semibold text-gray-700">
                      Last Name *
                    </label>
                    <input
                      type="text"
                      name="lastName"
                      value={formData.lastName}
                      onChange={handleChange}
                      className={`w-full px-4 py-3 rounded-xl border-2 transition-all duration-200 focus:outline-none focus:ring-4 focus:ring-primary-teaGreen/20 ${
                        errors.lastName 
                          ? 'border-red-300 focus:border-red-500' 
                          : 'border-gray-200 focus:border-primary-teaGreen'
                      }`}
                      placeholder="Last name"
                      required
                    />
                    {errors.lastName && (
                      <p className="text-sm text-red-600 flex items-center gap-1">
                        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                        </svg>
                        {errors.lastName}
                      </p>
                    )}
                  </div>
                </div>

                <div className="space-y-2">
                  <label className="block text-sm font-semibold text-gray-700">
                    Phone Number *
                  </label>
                  <input
                    type="number"
                    name="mobileNumber"
                    value={formData.mobileNumber}
                    onChange={handleChange}
                    className={`w-full px-4 py-3 rounded-xl border-2 transition-all duration-200 focus:outline-none focus:ring-4 focus:ring-primary-teaGreen/20 ${
                      errors.mobileNumber 
                        ? 'border-red-300 focus:border-red-500' 
                        : 'border-gray-200 focus:border-primary-teaGreen'
                    }`}
                    placeholder="Enter your phone number"
                    required
                  />
                  {errors.mobileNumber && (
                    <p className="text-sm text-red-600 flex items-center gap-1">
                      <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                      </svg>
                      {errors.mobileNumber}
                    </p>
                  )}
                </div>

                <Select
                  label="I am a "
                  options={[
                    { value: 'Jobseeker', label: 'Job Seeker' },
                    { value: 'Intern', label: 'Intern' },
                    { value: 'Company', label: 'Company' },
                    // { value: 'Agent', label: 'Agent' },
                  ]}
                  value={String(formData.role || '')}
                  onChange={(val) => setFormData(prev => ({ ...prev, role: val as LocalStorageUser['role'] }))}
                  placeholder="Select role"
                  required
                  className=""
                  native={false}
                  portal={false}
                  showSearchButton={false}
                  error={errors.role}
                />

                {(formData.role === 'Jobseeker' || formData.role === 'Intern') && (
                  <DatePicker
                    label="Date of Birth "
                    value={formData.dateOfBirth || ''}
                    onChange={(val) => {
                      if (typeof val == "string") {
                        setFormData((prev) => ({ ...prev, dateOfBirth: val }));
                      }
                    }
                    }
                    required
                    error={errors.dateOfBirth}
                  />
                )}

              </div>

              {errors.submit && (
                <div className="bg-red-50 border border-red-200 rounded-xl p-4">
                  <div className="flex items-center gap-2">
                    <svg className="w-5 h-5 text-red-500" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                    </svg>
                    <div>
                      <h3 className="text-sm font-medium text-red-800">Registration Failed</h3>
                      <p className="text-sm text-red-700">{errors.submit}</p>
                    </div>
                  </div>
                </div>
              )}

              <Button
                type="submit"
                size="lg"
                className="w-full py-4 text-lg font-semibold"
                loading={loading}
                disabled={loading}
              >
                {loading ? 'Creating account...' : 'Create Account'}
              </Button>

              <div className="text-center">
                <p className="text-sm text-gray-600">
                  Already have an account?{' '}
                  <Link to="/login" className="font-semibold text-primary-lightGreen">
                    Sign in here
                  </Link>
                </p>
              </div>
            </form>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Register;
